import { RegularShape, Circle, Fill, Style } from 'ol/style';

import { Stroke } from 'cadenza/map/style/stroke';
import { assertType } from 'cadenza/utils/custom-error';

import type Feature from 'ol/Feature';

// TODO_LATER (solovjova, 12.05.2020, CADENZA-15086 Routing-VectorLayer): selection styles (see https://conf.disy.net/x/eYepG)
// This is a minimal working solution to enable feature selection visualization for the vector
// layers rendered on the client side. Things to improve in the future:
// - dry with select-styles.js
// - improve point selection style, see for example 'styles[GeometryType.POINT]' in cadenza/map/style/select-styles.js

const DEFAULT_SELECTION_COLOR = [ 255, 255, 0, 0.8 ];
const SELECTION_STROKE = new Stroke({ color: DEFAULT_SELECTION_COLOR, width: 2 });
const SELECTION_FILL = new Fill({ color: DEFAULT_SELECTION_COLOR });

const STYLED_GEOMETRY_TYPES = [ 'Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon' ] as const;
export type StyledGeometryType = typeof STYLED_GEOMETRY_TYPES[number];
type StyleType = Record<StyledGeometryType, (f: Feature) => Style[] | undefined>;
const selectStyles: StyleType = {
  'Point': selectedPointStyle,
  'LineString': selectedLineStringStyle,
  'Polygon': selectedPolygonStyle,
  'MultiPoint': selectedPointStyle,
  'MultiLineString': selectedLineStringStyle,
  'MultiPolygon': selectedPolygonStyle
};

export function getSelectedFeatureStyle (feature: Feature) {
  if (!feature || !feature.getGeometry()) {
    return;
  }
  const geometryType = feature.getGeometry()!.getType(); // We check for undefined in the condition above
  assertType<keyof StyleType>(geometryType, geometryType in selectStyles);
  const style = selectStyles[geometryType];
  return (typeof style === 'function') ? style(feature) : style;
}

function selectedPointStyle (feature: Feature): Style[] {
  const styles = resolveFeatureStyles(feature);
  const existingImageStyleIndex = styles.findIndex(style => style.getImage());
  const existingImageStyle = existingImageStyleIndex >= 0 ? styles[existingImageStyleIndex] : null;

  if (existingImageStyle && existingImageStyle.getImage() instanceof RegularShape) {
    const selectedImageStyle = existingImageStyle.clone();
    (selectedImageStyle.getImage() as RegularShape).getFill()!.setColor(DEFAULT_SELECTION_COLOR);
    styles[existingImageStyleIndex] = selectedImageStyle;
  } else {
    styles.push(new Style({
      image: new Circle({
        stroke: SELECTION_STROKE,
        radius: 10
      })
    }));
  }
  return styles;
}

function selectedLineStringStyle (feature: Feature): Style[] {
  const styles = resolveFeatureStyles(feature);
  const existingStrokeStyleIndex = styles.findIndex(style => style.getStroke());

  // If stroke style exists, replace it with its clone, but with a selection color. Add an additional selection stroke style otherwise.
  if (existingStrokeStyleIndex >= 0) {
    const existingStrokeStyle = styles[existingStrokeStyleIndex];
    const selectedStrokeStyle = existingStrokeStyle.clone();
    selectedStrokeStyle.getStroke()!.setColor(DEFAULT_SELECTION_COLOR);
    styles[existingStrokeStyleIndex] = selectedStrokeStyle;
  } else {
    styles.push(new Style({
      stroke: SELECTION_STROKE
    }));
  }
  return styles;
}

function selectedPolygonStyle (feature: Feature): Style[] {
  const styles = resolveFeatureStyles(feature);
  const existingStrokeStyleIndex = styles.findIndex(style => style.getFill());

  // If fill style exists, replace it with its clone, but with a selection color. Add an additional selection fill style otherwise.
  if (existingStrokeStyleIndex >= 0) {
    const existingStrokeStyle = styles[existingStrokeStyleIndex];
    const selectedStrokeStyle = existingStrokeStyle.clone();
    selectedStrokeStyle.getFill()!.setColor(DEFAULT_SELECTION_COLOR);
    styles[existingStrokeStyleIndex] = selectedStrokeStyle;
  } else {
    styles.push(new Style({
      fill: SELECTION_FILL
    }));
  }
  return styles;
}

function resolveFeatureStyles (feature: Feature): Style[] {
  const styleOrStyles = feature.getStyle();
  if (!styleOrStyles) {
    return [];
  }
  // Our style is insensitive to map resolution, so this value is here only to comply with TS
  const styleInsensitiveMapResolution = 0;
  const styles = Array.isArray(styleOrStyles) ? styleOrStyles : [ styleOrStyles ];

  return styles.map(featureStyleOrGetter => {
    if (featureStyleOrGetter instanceof Function) {
      return featureStyleOrGetter(feature, styleInsensitiveMapResolution) || [];
    }
    return featureStyleOrGetter;
  }).flat();
}
